import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import styled from 'styled-components';
import { operationType } from "../../../../../services/types";
import { updateNodeValue } from "../../../../../services/method";
import { Textarea } from "../../../../form/textarea";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";

type MediaIntelligenceProps = NodeProps<operationType> & {
    setChildMinimize?: (fn: (id: string) => void) => void;
    setChildSettings?: (fn: (id: string) => void) => void;
};

const OPERATIONS = [
    { value: 'agent_decide', label: 'Let AI Agent Decide' },
    { value: 'generate_image', label: 'Generate Image' },
    { value: 'analyze_image', label: 'Analyze Image' },
];

const ANALYSIS_MODELS = ['gpt-4o', 'gemini-1.5-flash', 'gemini-1.5-pro'];

export function MediaIntelligence({ id, data, setChildMinimize, setChildSettings }: MediaIntelligenceProps) {
    const { setNodes } = useReactFlow();
    const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [uploadPreview, setUploadPreview] = React.useState<string>('');
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev); });
        if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setChildMinimize, setChildSettings]);

    React.useEffect(() => {
        if (!data.value) {
            setNodes((nds) => nds.map((node) => {
                if (node.id === id) return {
                    ...node,
                    data: {
                        ...node.data,
                        value: {
                            request: 'agent_decide',
                            prompt: '',
                            size: '1024x1024',
                            image_url: '',
                            question: '',
                            analysis_model: 'gpt-4o',
                        }
                    }
                };
                return node;
            }));
        }
        if (data.value?.image_url?.startsWith('data:image/')) {
            setUploadPreview(data.value.image_url);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const operation = data.value?.request || 'agent_decide';

    const handleSelect = (key: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
        updateNodeValue(id, setNodes, { [key]: e.target.value });
    };

    const handleText = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        updateNodeValue(id, setNodes, { [key]: e.target.value });
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const dataUrl = ev.target?.result as string;
            setUploadPreview(dataUrl);
            updateNodeValue(id, setNodes, { image_url: dataUrl });
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveUpload = () => {
        setUploadPreview('');
        updateNodeValue(id, setNodes, { image_url: '' });
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const isWarning = (operation === 'generate_image' && !data.value?.prompt) ||
        (operation === 'analyze_image' && !data.value?.image_url);

    let previewUrl = '';
    let previewPrompt = '';
    let previewMeta = '';
    if (data.output) {
        try {
            const parsed = JSON.parse(data.output);
            if (parsed?.type === 'image_generation' && parsed?.image_url) {
                previewUrl = parsed.image_url;
                previewPrompt = parsed.prompt || '';
                previewMeta = parsed.metadata ? `${parsed.metadata.model} · ${parsed.metadata.size}` : '';
            }
        } catch { /* not JSON */ }
    }

    return (<>
        <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
        <Main>
            {previewUrl && (
                <div className="node-image-preview">
                    <img src={previewUrl} alt={previewPrompt || 'Generated image'} />
                    <div className="node-image-meta">
                        {previewPrompt && <span className="node-image-prompt">{previewPrompt.length > 90 ? previewPrompt.slice(0, 90) + '…' : previewPrompt}</span>}
                        {previewMeta && <span className="node-image-info">{previewMeta}</span>}
                        <a href={previewUrl} download="generated-image.png" target="_blank" rel="noreferrer" className="node-download-btn">↓ Download</a>
                    </div>
                </div>
            )}
            {isWarning ? <>
                <div id="warning"></div>
                <span id="warningIcon"><IoWarningOutline size={21} /></span>
            </> : data.output ? <>
                <div id="ran"></div>
                <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
            </> : null}
            {isExpanded ? <ExpandedBlock
                data={{ id, ...data }}
                handleClose={() => setIsExpanded(false)}
                isWarning={isWarning}
                style={{ minWidth: '440px' }}
            ><Styled>

                <div className="field-group">
                    <label>Operation</label>
                    <select value={data.value?.request || 'agent_decide'} onChange={handleSelect('request')}>
                        {OPERATIONS.map(op => (
                            <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                    </select>
                </div>

                {(operation === 'generate_image' || operation === 'agent_decide') && (<>
                    <div className="section-divider">
                        <span className="section-title">✦ Image Generation</span>
                    </div>
                    <div className="field-group">
                        <label>Prompt</label>
                        <Textarea
                            value={data.value?.prompt || ''}
                            placeholder="Describe the image to generate..."
                            handleChange={(e: any) => updateNodeValue(id, setNodes, { prompt: e.target.value })}
                        />
                    </div>
                    <div className="row-2">
                        <div className="field-group">
                            <label>Model</label>
                            <div className="readonly-badge">GPT Image 1</div>
                        </div>
                        <div className="field-group">
                            <label>Size</label>
                            <div className="readonly-badge">1024 × 1024</div>
                        </div>
                    </div>
                    <div className="helper-text">Uses GPT Image 1 · Output as base64 PNG</div>
                </>)}

                {(operation === 'analyze_image' || operation === 'agent_decide') && (<>
                    <div className="section-divider">
                        <span className="section-title">🔍 Image Analysis</span>
                    </div>

                    {uploadPreview ? (
                        <div className="upload-preview">
                            <img src={uploadPreview} alt="Uploaded preview" />
                            <button className="remove-upload-btn" onClick={handleRemoveUpload}>✕ Remove</button>
                        </div>
                    ) : (
                        <div className="field-group">
                            <label>Image URL</label>
                            <input
                                type="text"
                                value={data.value?.image_url || ''}
                                placeholder="https://... or paste a URL"
                                onChange={handleText('image_url')}
                            />
                        </div>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                    />
                    <button className="upload-btn" onClick={() => fileInputRef.current?.click()}>
                        ↑ Upload Image
                    </button>

                    <div className="field-group">
                        <label>Question (optional)</label>
                        <input
                            type="text"
                            value={data.value?.question || ''}
                            placeholder="Describe this image in detail."
                            onChange={handleText('question')}
                        />
                    </div>
                    <div className="field-group">
                        <label>Analysis Model</label>
                        <select value={data.value?.analysis_model || 'gpt-4o'} onChange={handleSelect('analysis_model')}>
                            {ANALYSIS_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                </>)}

            </Styled></ExpandedBlock> : null}
        </Main>
    </>);
}

const Main = styled.div`
    min-width: 150px;
    padding: 0;

    .node-image-preview {
        display: flex;
        flex-direction: column;
        border-radius: 10px;
        overflow: hidden;
        border: 1px solid #e5e7eb;
        background: #f9fafb;
        margin-bottom: 4px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.07);

        img {
            width: 100%;
            max-height: 220px;
            object-fit: contain;
            display: block;
            background: #f9fafb;
        }

        .node-image-meta {
            display: flex;
            flex-direction: column;
            gap: 3px;
            padding: 8px 10px;
            background: #fff;
            border-top: 1px solid #e5e7eb;
        }

        .node-image-prompt {
            font-size: 11px;
            color: #4b5563;
            line-height: 1.4;
        }

        .node-image-info {
            font-size: 10px;
            color: #9ca3af;
            font-variant-numeric: tabular-nums;
        }

        .node-download-btn {
            font-size: 11px;
            color: #7c3aed;
            font-weight: 600;
            text-decoration: none;
            border: 1px solid #ddd6fe;
            border-radius: 5px;
            padding: 3px 10px;
            align-self: flex-start;
            margin-top: 3px;
            transition: all 0.15s ease;

            &:hover {
                background: linear-gradient(135deg, #6d28d9, #7c3aed);
                border-color: transparent;
                color: #fff;
            }
        }
    }
`;

const Styled = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;

    .section-divider {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 4px 0 2px;

        &::before, &::after {
            content: '';
            flex: 1;
            height: 1px;
            background: linear-gradient(90deg, transparent, #e5e7eb);
        }
        &::before { background: linear-gradient(90deg, #e5e7eb, transparent); }
    }

    .section-title {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        background: linear-gradient(135deg, #6d28d9, #4f46e5);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        white-space: nowrap;
    }

    .field-group {
        display: flex;
        flex-direction: column;
        gap: 5px;

        label {
            font-size: 11.5px;
            font-weight: 600;
            color: #6b7280;
            letter-spacing: 0.01em;
        }

        select, input[type="text"] {
            font-size: 13px;
            padding: 7px 10px;
            border: 1px solid #e5e7eb;
            border-radius: 9px;
            background: #fafafa;
            color: #111827;
            outline: none;
            width: 100%;
            transition: border-color 0.15s, box-shadow 0.15s;

            &:focus {
                border-color: #7c3aed;
                box-shadow: 0 0 0 3px rgba(124,58,237,0.1);
                background: #fff;
            }
        }
    }

    .readonly-badge {
        font-size: 12.5px;
        font-weight: 600;
        padding: 6px 10px;
        border-radius: 9px;
        background: linear-gradient(135deg, #f5f3ff, #ede9fe);
        border: 1px solid #ddd6fe;
        color: #5b21b6;
        text-align: center;
        cursor: default;
        user-select: none;
    }

    .row-2 {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
    }

    .helper-text {
        font-size: 11px;
        color: #9ca3af;
        padding: 4px 2px 0;
        font-style: italic;
    }

    .upload-btn {
        font-size: 12.5px;
        font-weight: 600;
        padding: 7px 14px;
        border-radius: 9px;
        border: 1.5px solid #7c3aed;
        background: transparent;
        color: #7c3aed;
        cursor: pointer;
        transition: all 0.15s ease;
        align-self: flex-start;

        &:hover {
            background: #7c3aed;
            color: #fff;
        }
    }

    .upload-preview {
        position: relative;
        border-radius: 10px;
        overflow: hidden;
        border: 1.5px solid #ddd6fe;
        box-shadow: 0 2px 8px rgba(124,58,237,0.1);

        img {
            width: 100%;
            max-height: 180px;
            object-fit: contain;
            display: block;
            background: #f5f3ff;
        }

        .remove-upload-btn {
            position: absolute;
            top: 6px;
            right: 6px;
            font-size: 11px;
            font-weight: 600;
            padding: 3px 9px;
            border-radius: 6px;
            border: none;
            background: rgba(0,0,0,0.55);
            color: #fff;
            cursor: pointer;
            transition: background 0.15s;

            &:hover { background: rgba(220,38,38,0.85); }
        }
    }
`;

